import { motion } from "framer-motion";
import { Quote, CheckCircle2, Info } from "lucide-react";

interface ArticleSection {
  type: "intro" | "heading" | "paragraph" | "list" | "quote" | "table";
  content?: string;
  title?: string;
  items?: string[];
  headers?: string[];
  rows?: string[][];
  author?: string;
}

interface ArticleContentProps {
  sections: ArticleSection[];
}

export const ArticleContent = ({ sections }: ArticleContentProps) => {
  const renderSection = (section: ArticleSection, index: number) => {
    switch (section.type) {
      case "intro":
        return (
          <motion.div
            key={index}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1 }}
            className="relative mb-8"
          >
            <div className="absolute -left-4 top-0 bottom-0 w-1 bg-gradient-to-b from-primary to-primary/30 rounded-full" />
            <p className="text-lg text-foreground/90 leading-relaxed pl-4 font-medium">
              {section.content}
            </p>
          </motion.div>
        );

      case "heading":
        return (
          <motion.h2
            key={index}
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: index * 0.1 }}
            className="text-xl font-bold text-foreground mt-8 mb-4 flex items-center gap-3"
          >
            <div className="w-2 h-2 rounded-full bg-primary" />
            {section.title}
          </motion.h2>
        );

      case "paragraph":
        return (
          <motion.p
            key={index}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: index * 0.1 }}
            className="text-muted-foreground leading-relaxed mb-4"
          >
            {section.content}
          </motion.p>
        );

      case "list":
        return (
          <motion.ul
            key={index}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: index * 0.1 }}
            className="space-y-3 mb-6"
          >
            {section.items?.map((item, i) => (
              <li key={i} className="flex items-start gap-3">
                <CheckCircle2 className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" />
                <span className="text-muted-foreground">{item}</span>
              </li>
            ))}
          </motion.ul>
        );

      case "quote":
        return (
          <motion.blockquote
            key={index}
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: index * 0.1 }}
            className="relative my-8 p-6 bg-gradient-to-br from-primary/10 to-primary/5 rounded-2xl border border-primary/20"
          >
            <Quote className="absolute top-4 left-4 w-8 h-8 text-primary/30" />
            <p className="text-foreground italic pl-8 text-lg leading-relaxed">
              "{section.content}"
            </p>
            {section.author && (
              <p className="text-sm text-muted-foreground mt-3 pl-8">
                — {section.author}
              </p>
            )}
          </motion.blockquote>
        );

      case "table":
        return (
          <motion.div
            key={index}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: index * 0.1 }}
            className="my-6 overflow-x-auto"
          >
            <table className="w-full border-collapse">
              <thead>
                <tr className="bg-muted/50">
                  {section.headers?.map((header, i) => (
                    <th
                      key={i}
                      className="text-left p-3 text-sm font-semibold text-foreground border-b border-border"
                    >
                      {header}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {section.rows?.map((row, rowIndex) => (
                  <tr key={rowIndex} className="hover:bg-muted/30 transition-colors">
                    {row.map((cell, cellIndex) => (
                      <td
                        key={cellIndex}
                        className="p-3 text-sm text-muted-foreground border-b border-border/50"
                      >
                        {cell}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </motion.div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="prose prose-sm max-w-none">
      {sections.map((section, index) => renderSection(section, index))}
      
      {/* Bottom info card */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: sections.length * 0.1 }}
        className="mt-8 p-4 bg-muted/50 rounded-xl flex items-start gap-3"
      >
        <Info className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" />
        <p className="text-sm text-muted-foreground">
          Artikel ini adalah bagian dari sistem bantuan SORIN. Hubungi administrator jika Anda memiliki pertanyaan lebih lanjut.
        </p>
      </motion.div>
    </div>
  );
};
